import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { sensorService } from '../services/sensorService';
import { growerService } from '../services/growerService';
import { locationService } from '../services/locationService';
import { ttiSettingsService } from '../services/ttiSettingsService';
import { ttiApplicationKeysService } from '../services/ttiApplicationKeysService';
import { userService, SavedFilter } from '../services/userService';
import Layout from '../components/Layout';
import { 
  Sensor, 
  User, 
  Grower,
  Location,
  TTIRegistrationRequest, 
  TTIApplication,
  GrowerSensorCreate,
  ImageDeviceDataResponse,
  TTIDevice,
  ConnectTTIDeviceRequest,
  TTISettingsResponse,
  ConnectTTIDeviceWithStoredKeyRequest,
  TTIApplicationKey,
  CSVColumnInfo,
  CSVBatchImportResponse
} from '../types';

const FREQUENCY_PLAN_BY_REGION: Record<string, string> = {
  eu1: 'EU_863_870',
  eu2: 'EU_863_870',
  nam1: 'US_902_928_FSB_2',
  au1: 'AU_915_928',
  as1: 'AS_923',
  as2: 'AS_923',
};

const normalizeTTIDeviceId = (deviceId: string) => deviceId
  .toLowerCase()
  .replace(/[^a-z0-9-]/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '');

const Sensors: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const getFrequencyPlanLabel = (frequencyPlanId: string) => {
    const translationKeys: Record<string, string> = {
      EU_863_870: 'sensors.eu863870',
      US_902_928_FSB_2: 'sensors.us902928',
      AU_915_928: 'sensors.au915928',
      AS_923: 'sensors.as923',
    };
    return translationKeys[frequencyPlanId] ? t(translationKeys[frequencyPlanId]) : frequencyPlanId;
  };
  const [user, setUser] = useState<User | null>(null);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [pendingSensors, setPendingSensors] = useState<Sensor[]>([]);
  const [unassignedSensors, setUnassignedSensors] = useState<Sensor[]>([]);
  const [growers, setGrowers] = useState<Grower[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTTIModal, setShowTTIModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showGrowerCreateModal, setShowGrowerCreateModal] = useState(false);
  const [showConnectTTIModal, setShowConnectTTIModal] = useState(false);
  const [showConnectStoredKeyModal, setShowConnectStoredKeyModal] = useState(false);
  const [showAssignGrowerModal, setShowAssignGrowerModal] = useState(false);
  // Bulk grower assignment state
  const [selectedUnassignedIds, setSelectedUnassignedIds] = useState<Set<number>>(new Set());
  const [showBulkAssignGrowerModal, setShowBulkAssignGrowerModal] = useState(false);
  const [bulkAssignGrowerId, setBulkAssignGrowerId] = useState<number | null>(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [editingSensor, setEditingSensor] = useState<Sensor | null>(null);
  const [ttiSensor, setTTISensor] = useState<Sensor | null>(null);
  const [ttiApplications, setTTIApplications] = useState<TTIApplication[]>([]);
  const [ttiDevices, setTTIDevices] = useState<TTIDevice[]>([]);
  const [loadingTTIApps, setLoadingTTIApps] = useState(false);
  const [loadingTTIDevices, setLoadingTTIDevices] = useState(false);
  const [imageExtractedData, setImageExtractedData] = useState<ImageDeviceDataResponse | null>(null);
  const [extractingImage, setExtractingImage] = useState(false);
  const [ttiImageExtractedData, setTTIImageExtractedData] = useState<ImageDeviceDataResponse | null>(null);
  const [ttiExtractingImage, setTTIExtractingImage] = useState(false);
  const [ttiSettings, setTTISettings] = useState<TTISettingsResponse | null>(null);
  const [applicationKeys, setApplicationKeys] = useState<TTIApplicationKey[]>([]);
  // CSV Batch Import state
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [csvColumns, setCsvColumns] = useState<CSVColumnInfo[]>([]);
  const [csvExampleRow, setCsvExampleRow] = useState('');
  const [csvImportResults, setCsvImportResults] = useState<CSVBatchImportResponse | null>(null);
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [csvImportError, setCsvImportError] = useState('');
  const [csvSelectedAppId, setCsvSelectedAppId] = useState('');
  const [csvSelectedFile, setCsvSelectedFile] = useState<File | null>(null);
  const [csvColumnsExpanded, setCsvColumnsExpanded] = useState(false);
  const [unassignedCollapsed, setUnassignedCollapsed] = useState(true);
  const [defaultParMultiplier, setDefaultParMultiplier] = useState(0.45);
  const [formData, setFormData] = useState({
    device_id: '',
    name: '',
    description: '',
    location: '',
    location_id: null as number | null,
    sensor_type: 'LBSE-01',
    par_multiplier: '0.45',
    grower_id: null as number | null,
    tank_height_mm: '' as string,
    sensor_height_mm: '' as string,
    max_fill_height_mm: '' as string,
  });
  
  // TTI registration option for Add Sensor dialog
  const [registerWithTTIOnCreate, setRegisterWithTTIOnCreate] = useState(false);
  const [growerFormData, setGrowerFormData] = useState<GrowerSensorCreate>({
    device_id: '',
    name: '',
    description: '',
    location: '',
    sensor_type: 'LBSE-01',
    dev_eui: '',
    join_eui: '',
    app_key: '',
  });
  const [ttiFormData, setTTIFormData] = useState({
    api_key: '',
    app_id: '',
    region: 'nam1',
    device_id: '',
    dev_eui: '',
    join_eui: '',
    app_key: '',
    frequency_plan_id: 'US_902_928_FSB_2',
  });
  const [connectTTIFormData, setConnectTTIFormData] = useState<ConnectTTIDeviceRequest>({
    api_key: '',
    app_id: '',
    region: 'nam1',
    device_id: '',
    name: '',
    description: '',
    location: '',
    sensor_type: 'LBSE-01',
    grower_id: null,
  });
  const [connectStoredKeyFormData, setConnectStoredKeyFormData] = useState<ConnectTTIDeviceWithStoredKeyRequest>({
    app_id: '',
    device_id: '',
    name: '',
    description: '',
    location: '',
    sensor_type: 'SE01-LB',
    grower_id: null,
  });
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(new Set());
  const [sharedSensorType, setSharedSensorType] = useState('SE01-LB');
  const [sharedGrowerId, setSharedGrowerId] = useState<number | null>(null);
  const [connectResults, setConnectResults] = useState<{ device_id: string; success: boolean; message: string }[]>([]);
  const [hideConnectedDevices, setHideConnectedDevices] = useState(true);
  const [assignGrowerFormData, setAssignGrowerFormData] = useState<{ grower_id: number | null }>({
    grower_id: null,
  });
  const [locationFormData, setLocationFormData] = useState({
    location: '',
    location_id: null as number | null,
  });
  const [error, setError] = useState('');
  const [ttiError, setTTIError] = useState('');
  const [ttiSuccess, setTTISuccess] = useState('');
  const [ttiLoading, setTTILoading] = useState(false);
  const [connectTTIError, setConnectTTIError] = useState('');
  const [connectTTILoading, setConnectTTILoading] = useState(false);
  
  // Search and Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterApplication, setFilterApplication] = useState<string>('all');
  const [filterGrower, setFilterGrower] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterTTIStatus, setFilterTTIStatus] = useState<string>('all');
  const [filterRegion, setFilterRegion] = useState<string>('all');
  const [filterSensorType, setFilterSensorType] = useState<string>('all');
  const [filterGateway, setFilterGateway] = useState<string>('all');

  // Pagination state
  const [sensorPage, setSensorPage] = useState(1);
  const [sensorsPerPage, setSensorsPerPage] = useState(25);

  // Saved filters state
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [lastUsedFilterName, setLastUsedFilterName] = useState<string | null>(null);
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [filterNameInput, setFilterNameInput] = useState('');
  // 'update' = overwrite active filter, 'new' = save under a new name
  const [saveFilterMode, setSaveFilterMode] = useState<'update' | 'new'>('update');

  const openSaveFilterModal = () => {
    setSaveFilterMode(lastUsedFilterName ? 'update' : 'new');
    setFilterNameInput('');
    setShowSaveFilterModal(true);
  };

  const handleSaveFilter = async () => {
    const name = saveFilterMode === 'update' && lastUsedFilterName
      ? lastUsedFilterName
      : filterNameInput.trim();
    if (!name) return;
    const filter: SavedFilter = {
      name,
      searchTerm,
      filterApplication,
      filterGrower,
      filterStatus,
      filterTTIStatus,
      filterRegion,
      filterSensorType,
      filterGateway: isAdmin ? filterGateway : 'all',
    };
    try {
      const result = await userService.upsertSavedFilter(filter);
      setSavedFilters(result.saved_filters);
      setLastUsedFilterName(result.last_used_filter);
      await loadData();
    } catch (err) {
      console.error('Failed to save filter', err);
    }
    setShowSaveFilterModal(false);
    setFilterNameInput('');
  };


  const handleLoadFilter = async (filter: SavedFilter) => {
    setSearchTerm(filter.searchTerm);
    setFilterApplication(filter.filterApplication);
    setFilterGrower(filter.filterGrower);
    setFilterStatus(filter.filterStatus);
    setFilterTTIStatus(filter.filterTTIStatus);
    setFilterRegion(filter.filterRegion);
    setFilterSensorType(filter.filterSensorType || 'all');
    setFilterGateway(user?.is_admin ? (filter.filterGateway || 'all') : 'all');
    setLastUsedFilterName(filter.name);
    try {
      await userService.setLastUsedFilter(filter.name);
    } catch (err) {
      console.error('Failed to set last used filter', err);
    }
  };

  const handleDeleteSavedFilter = async (name: string) => {
    try {
      const result = await userService.deleteSavedFilter(name);
      setSavedFilters(result.saved_filters);
      setLastUsedFilterName(result.last_used_filter);
    } catch (err) {
      console.error('Failed to delete filter', err);
    }
  };

  const [connectStoredKeyError, setConnectStoredKeyError] = useState('');
  const [connectStoredKeyLoading, setConnectStoredKeyLoading] = useState(false);

  // Track whether we've already applied the last-used filter on first load
  const [filtersBootstrapped, setFiltersBootstrapped] = useState(false);

  useEffect(() => {
    loadData();
    sensorService.getSensorDefaults()
      .then(({ par_multiplier }) => setDefaultParMultiplier(par_multiplier))
      .catch((error) => console.error('Failed to load sensor defaults', error));
  }, []);

  const loadData = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);

      // Load saved filters once and auto-apply last-used on first load
      try {
        const filterData = await userService.getSavedFilters();
        setSavedFilters(filterData.saved_filters);
        setLastUsedFilterName(filterData.last_used_filter);
        if (!filtersBootstrapped && filterData.last_used_filter) {
          const last = filterData.saved_filters.find(f => f.name === filterData.last_used_filter);
          if (last) {
            setSearchTerm(last.searchTerm);
            setFilterApplication(last.filterApplication);
            setFilterGrower(last.filterGrower);
            setFilterStatus(last.filterStatus);
            setFilterTTIStatus(last.filterTTIStatus);
            setFilterRegion(last.filterRegion);
            setFilterSensorType(last.filterSensorType || 'all');
            setFilterGateway(currentUser.is_admin ? (last.filterGateway || 'all') : 'all');
          }
        }
        setFiltersBootstrapped(true);
      } catch (err) {
        // Non-critical - continue loading
      }

      if (currentUser.is_admin) {
        const [sensorsData, growersData, pendingData, unassignedData, locationsData] = await Promise.all([
          sensorService.getSensors(),
          growerService.getGrowers(),
          sensorService.getPendingSensors().catch(() => []),
          sensorService.getUnassignedSensors().catch(() => []),
          locationService.getLocations().catch(() => []),
        ]);
        setSensors(sensorsData);
        setGrowers(growersData);
        setPendingSensors(pendingData);
        setUnassignedSensors(unassignedData);
        setLocations(locationsData);
        
        // Try to load TTI settings (legacy)
        try {
          const settings = await ttiSettingsService.getSettings();
          setTTISettings(settings);
        } catch (err) {
          // Settings not configured - that's ok
          setTTISettings(null);
        }

        // Load TTI Application Keys
        try {
          const response = await ttiApplicationKeysService.listApplicationKeys();
          setApplicationKeys(response.application_keys);
        } catch (err) {
          console.error('Failed to load application keys:', err);
          setApplicationKeys([]);
        }
      } else if (currentUser.is_admin_grower) {
        // Grower admins need grower data to show assignment labels
        const [sensorsData, growersData, locationsData] = await Promise.all([
          sensorService.getSensors(),
          growerService.getGrowers(),
          locationService.getLocations().catch(() => []),
        ]);
        setSensors(sensorsData);
        setGrowers(growersData);
        setLocations(locationsData);
      } else {
        const [sensorsData, locationsData] = await Promise.all([
          sensorService.getSensors(),
          locationService.getLocations().catch(() => []),
        ]);
        setSensors(sensorsData);
        setLocations(locationsData);
        // Load growers for users assigned to multiple growers (for filter dropdown)
        if (currentUser.grower_ids && currentUser.grower_ids.length > 1) {
          growerService.getGrowers().then(setGrowers).catch(() => {});
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load sensors');
    } finally {
      setLoading(false);
    }
  };

  // Get unique applications from sensors for filter dropdown
  const uniqueApplications = Array.from(new Set(sensors.map(s => s.tti_app_id).filter(Boolean))) as string[];

  // Get unique regions from sensors for filter dropdown
  const uniqueRegions = Array.from(new Set(sensors.map(s => s.tti_region).filter(Boolean))) as string[];

  // Get unique sensor types from sensors for filter dropdown
  const uniqueSensorTypes = Array.from(new Set(sensors.map(s => s.sensor_type).filter(Boolean))) as string[];

  const uniqueGateways = Array.from(
    sensors.reduce((gateways, sensor) => {
      if (sensor.last_seen_gateway_id && !gateways.has(sensor.last_seen_gateway_id)) {
        gateways.set(
          sensor.last_seen_gateway_id,
          sensor.last_seen_gateway_internal_name || sensor.last_seen_gateway_id,
        );
      }
      return gateways;
    }, new Map<string, string>()),
  ).sort(([, firstLabel], [, secondLabel]) => firstLabel.localeCompare(secondLabel));

  // Filtered sensors based on search and filters
  const filteredSensors = sensors.filter(sensor => {
    // Search filter (device_id or name)
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      sensor.device_id.toLowerCase().includes(searchLower) || 
      sensor.name.toLowerCase().includes(searchLower);

    // Application filter
    const matchesApplication = filterApplication === 'all' || 
      (filterApplication === 'unassigned' && !sensor.tti_app_id) ||
      sensor.tti_app_id === filterApplication;

    // Grower filter
    const matchesGrower = filterGrower === 'all' || 
      (filterGrower === 'unassigned' && !sensor.grower_id) ||
      sensor.grower_id?.toString() === filterGrower;

    // Status filter (active/inactive)
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && sensor.is_active) ||
      (filterStatus === 'inactive' && !sensor.is_active);

    // TTI Status filter
    const matchesTTIStatus = filterTTIStatus === 'all' || 
      (filterTTIStatus === 'registered' && sensor.tti_registered) ||
      (filterTTIStatus === 'not_registered' && !sensor.tti_registered);

    // Region filter
    const matchesRegion = filterRegion === 'all' ||
      (filterRegion === 'unassigned' && !sensor.tti_region) ||
      sensor.tti_region === filterRegion;

    // Sensor Type filter
    const matchesSensorType = filterSensorType === 'all' ||
      sensor.sensor_type === filterSensorType;

    const matchesGateway = filterGateway === 'all'
      || (filterGateway === 'unassigned' && !sensor.last_seen_gateway_id)
      || sensor.last_seen_gateway_id === filterGateway;

    return matchesSearch && matchesApplication && matchesGrower && matchesStatus && matchesTTIStatus && matchesRegion && matchesSensorType && matchesGateway;
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterApplication('all');
    setFilterGrower('all');
    setFilterStatus('all');
    setFilterTTIStatus('all');
    setFilterRegion('all');
    setFilterSensorType('all');
    setFilterGateway('all');
    setSensorPage(1);
  };

  // Pagination derived values
  const sensorTotalPages = Math.max(1, Math.ceil(filteredSensors.length / sensorsPerPage));
  const paginatedSensors = filteredSensors.slice((sensorPage - 1) * sensorsPerPage, sensorPage * sensorsPerPage);

  // Load TTI applications when API key and region are provided
  const handleLoadTTIApplications = async () => {
    if (!ttiFormData.api_key || !ttiFormData.region) {
      setTTIError('Please enter API key and select region first');
      return;
    }
    
    setLoadingTTIApps(true);
    setTTIError('');
    
    try {
      const response = await sensorService.listTTIApplications({
        api_key: ttiFormData.api_key,
        region: ttiFormData.region,
      });
      setTTIApplications(response.applications);
    } catch (err: any) {
      setTTIError(err.response?.data?.detail || 'Failed to load TTI applications');
    } finally {
      setLoadingTTIApps(false);
    }
  };

  // Handle TTI activation toggle
  const handleTTIActivation = async (sensor: Sensor, activate: boolean) => {
    const action = activate ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} this sensor on TTI?`)) {
      return;
    }

    try {
      await sensorService.setTTIActivation(sensor.id, { activate });
      await loadData();
      alert(`Sensor ${action}d successfully on TTI`);
    } catch (err: any) {
      alert(err.response?.data?.detail || `Failed to ${action} sensor`);
    }
  };

  // Handle sensor approval/rejection
  const handleApproveSensor = async (sensorId: number) => {
    if (!window.confirm('Are you sure you want to approve this sensor?')) {
      return;
    }

    try {
      await sensorService.approveSensor(sensorId);
      await loadData();
      alert('Sensor approved successfully');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to approve sensor');
    }
  };

  const handleRejectSensor = async (sensorId: number) => {
    if (!window.confirm('Are you sure you want to reject and delete this sensor?')) {
      return;
    }

    try {
      await sensorService.rejectSensor(sensorId);
      await loadData();
      alert('Sensor rejected and deleted');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to reject sensor');
    }
  };

  // Handle image upload and extraction
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setExtractingImage(true);
    setImageExtractedData(null);
    setError('');

    try {
      const result = await sensorService.extractDeviceDataFromImage(file);
      setImageExtractedData(result);
      
      // Pre-fill form data if extraction was successful
      if (result.device_id) {
        setGrowerFormData(prev => ({ ...prev, device_id: result.device_id || '' }));
      }
      if (result.dev_eui) {
        setGrowerFormData(prev => ({ ...prev, dev_eui: result.dev_eui || '' }));
      }
      if (result.join_eui) {
        setGrowerFormData(prev => ({ ...prev, join_eui: result.join_eui || '' }));
      }
      if (result.app_key) {
        setGrowerFormData(prev => ({ ...prev, app_key: result.app_key || '' }));
      }
      if (result.device_name) {
        setGrowerFormData(prev => ({ ...prev, name: result.device_name || '' }));
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to extract device data from image');
    } finally {
      setExtractingImage(false);
    }
  };

  // Grower create sensor handlers
  const handleOpenGrowerCreateModal = () => {
    setGrowerFormData({
      device_id: '',
      name: '',
      description: '',
      location: '',
      sensor_type: 'LBSE-01',
      dev_eui: '',
      join_eui: '',
      app_key: '',
    });
    setImageExtractedData(null);
    setShowGrowerCreateModal(true);
    setError('');
  };

  const handleCloseGrowerCreateModal = () => {
    setShowGrowerCreateModal(false);
    setImageExtractedData(null);
    setError('');
  };

  const handleGrowerCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await sensorService.createSensorAsGrower(growerFormData);
      handleCloseGrowerCreateModal();
      await loadData();
      alert('Sensor submitted for admin approval');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create sensor');
    }
  };

  // Connect existing TTI device handlers
  const handleOpenConnectTTIModal = () => {
    setConnectTTIFormData({
      api_key: '',
      app_id: '',
      region: 'nam1',
      device_id: '',
      name: '',
      description: '',
      location: '',
      sensor_type: 'LBSE-01',
      grower_id: null,
    });
    setTTIDevices([]);
    setTTIApplications([]);
    setConnectTTIError('');
    setShowConnectTTIModal(true);
  };

  const handleCloseConnectTTIModal = () => {
    setShowConnectTTIModal(false);
    setTTIDevices([]);
    setConnectTTIError('');
  };

  const handleLoadTTIDevices = async () => {
    if (!connectTTIFormData.api_key || !connectTTIFormData.app_id) {
      setConnectTTIError('Please enter API key and Application ID first');
      return;
    }

    setLoadingTTIDevices(true);
    setConnectTTIError('');

    try {
      const response = await sensorService.listTTIDevices({
        api_key: connectTTIFormData.api_key,
        app_id: connectTTIFormData.app_id,
        region: connectTTIFormData.region,
      });
      setTTIDevices(response.devices);
    } catch (err: any) {
      setConnectTTIError(err.response?.data?.detail || 'Failed to load TTI devices');
    } finally {
      setLoadingTTIDevices(false);
    }
  };

  const handleSelectTTIDevice = (device: TTIDevice) => {
    setConnectTTIFormData(prev => ({
      ...prev,
      device_id: device.device_id,
      name: device.name || device.device_id,
      description: device.description || '',
    }));
  };

  const handleConnectTTISubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setConnectTTIError('');
    setConnectTTILoading(true);

    try {
      await sensorService.connectTTIDevice(connectTTIFormData);
      handleCloseConnectTTIModal();
      await loadData();
      alert('Device connected successfully!');
    } catch (err: any) {
      setConnectTTIError(err.response?.data?.detail || 'Failed to connect device');
    } finally {
      setConnectTTILoading(false);
    }
  };

  // Connect TTI device with stored API key handlers
  const handleOpenConnectStoredKeyModal = async () => {
    setConnectStoredKeyFormData({ app_id: '', device_id: '', name: '', description: '', location: '', sensor_type: 'SE01-LB', grower_id: null });
    setSelectedDeviceIds(new Set());
    setSharedSensorType('SE01-LB');
    setSharedGrowerId(null);
    setConnectResults([]);
    setTTIDevices([]);
    setConnectStoredKeyError('');
    setHideConnectedDevices(true);
    setShowConnectStoredKeyModal(true);

    if (applicationKeys.length === 0) {
      setLoadingTTIApps(true);
      try {
        const response = await ttiSettingsService.listApplications();
        setTTIApplications(response.applications);
      } catch (err: any) {
        setConnectStoredKeyError(err.response?.data?.detail || 'Failed to load TTI applications.');
      } finally {
        setLoadingTTIApps(false);
      }
    } else {
      setTTIApplications([]);
    }
  };

  const handleCloseConnectStoredKeyModal = () => {
    setShowConnectStoredKeyModal(false);
    setTTIDevices([]);
    setTTIApplications([]);
    setConnectStoredKeyError('');
    setSelectedDeviceIds(new Set());
    setConnectResults([]);
  };

  // Load devices from application key
  const handleLoadTTIDevicesFromAppKey = async (appKeyId: number) => {
    setLoadingTTIDevices(true);
    setConnectStoredKeyError('');
    try {
      const response = await ttiApplicationKeysService.listDevicesForApplication(appKeyId);
      setTTIDevices(response.devices);
    } catch (err: any) {
      setConnectStoredKeyError(err.response?.data?.detail || 'Failed to load TTI devices');
    } finally {
      setLoadingTTIDevices(false);
    }
  };

  const handleConnectStoredKeySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDeviceIds.size === 0) return;
    setConnectStoredKeyError('');
    setConnectStoredKeyLoading(true);
    setConnectResults([]);

    const results: { device_id: string; success: boolean; message: string }[] = [];
    for (const deviceId of Array.from(selectedDeviceIds)) {
      const device = ttiDevices.find(d => d.device_id === deviceId);
      try {
        await sensorService.connectTTIDeviceWithStoredKey({
          app_id: connectStoredKeyFormData.app_id,
          device_id: deviceId,
          name: device?.name || deviceId,
          description: device?.description || '',
          sensor_type: sharedSensorType,
          grower_id: sharedGrowerId,
        });
        results.push({ device_id: deviceId, success: true, message: 'Connected successfully' });
      } catch (err: any) {
        results.push({ device_id: deviceId, success: false, message: err.response?.data?.detail || 'Failed to connect' });
      }
    }

    setConnectResults(results);
    setConnectStoredKeyLoading(false);
    await loadData();

    if (results.every(r => r.success)) {
      handleCloseConnectStoredKeyModal();
    } else {
      // Keep modal open — remove successful ones from selection so user can see what failed
      const failedIds = new Set(results.filter(r => !r.success).map(r => r.device_id));
      setSelectedDeviceIds(failedIds);
    }
  };


  const handleCloseCSVImportModal = () => {
    setShowCSVImportModal(false);
    setCsvImportError('');
    setCsvImportResults(null);
    setCsvSelectedAppId('');
    setCsvSelectedFile(null);
  };

  const handleCSVFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCsvSelectedFile(file);
      setCsvImportResults(null);
      setCsvImportError('');
    }
  };

  const handleCSVImportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCsvImportError('');
    setCsvImportResults(null);
    
    if (!csvSelectedFile) {
      setCsvImportError('Please select a CSV file');
      return;
    }
    
    if (!csvSelectedAppId) {
      setCsvImportError('Please select a TTI application');
      return;
    }
    
    setCsvImportLoading(true);
    
    try {
      const results = await sensorService.batchRegisterFromCSV(csvSelectedFile, csvSelectedAppId);
      setCsvImportResults(results);
      
      // Reload data if any devices were successfully imported
      if (results.successful > 0) {
        await loadData();
      }
    } catch (err: any) {
      setCsvImportError(err.response?.data?.detail || 'Failed to import devices from CSV');
    } finally {
      setCsvImportLoading(false);
    }
  };

  // Sensor-Grower assignment handlers
  const handleOpenAssignGrowerModal = (sensor: Sensor) => {
    setEditingSensor(sensor);
    setAssignGrowerFormData({
      grower_id: sensor.grower_id,
    });
    setShowAssignGrowerModal(true);
    setError('');
  };

  const handleCloseAssignGrowerModal = () => {
    setShowAssignGrowerModal(false);
    setEditingSensor(null);
    setError('');
  };

  const handleAssignGrowerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!editingSensor) return;

    try {
      await sensorService.assignSensorToGrower(editingSensor.id, assignGrowerFormData);
      handleCloseAssignGrowerModal();
      await loadData();
      alert(assignGrowerFormData.grower_id ? 'Sensor assigned to grower' : 'Sensor unassigned from grower');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update sensor assignment');
    }
  };

  const toggleUnassignedSelection = (id: number) => {
    setSelectedUnassignedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkAssignGrowerSubmit = async () => {
    if (selectedUnassignedIds.size === 0 || bulkAssignGrowerId === null) return;
    setBulkAssigning(true);
    try {
      await Promise.all(
        Array.from(selectedUnassignedIds).map((id) =>
          sensorService.assignSensorToGrower(id, { grower_id: bulkAssignGrowerId })
        )
      );
      setSelectedUnassignedIds(new Set());
      setShowBulkAssignGrowerModal(false);
      setBulkAssignGrowerId(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to bulk assign sensors');
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleOpenModal = (sensor?: Sensor) => {
    if (sensor) {
      setEditingSensor(sensor);
      setFormData({
        device_id: sensor.device_id,
        name: sensor.name,
        description: sensor.description || '',
        location: sensor.location || '',
        location_id: sensor.location_id ?? null,
        sensor_type: sensor.sensor_type,
        par_multiplier: String(sensor.par_multiplier ?? defaultParMultiplier),
        grower_id: sensor.grower_id,
        tank_height_mm: sensor.tank_height_mm != null ? String(sensor.tank_height_mm) : '',
        sensor_height_mm: sensor.sensor_height_mm != null ? String(sensor.sensor_height_mm) : '',
        max_fill_height_mm: sensor.max_fill_height_mm != null ? String(sensor.max_fill_height_mm) : '',
      });
    } else {
      setEditingSensor(null);
      setFormData({
        device_id: '',
        name: '',
        description: '',
        location: '',
        location_id: null,
        sensor_type: 'LBSE-01',
        par_multiplier: String(defaultParMultiplier),
        grower_id: null,
        tank_height_mm: '',
        sensor_height_mm: '',
        max_fill_height_mm: '',
      });
      // Reset TTI registration option for new sensor
      setRegisterWithTTIOnCreate(false);
    }
    setShowModal(true);
    setError('');
  };

  const handleOpenLocationModal = (sensor: Sensor) => {
    setEditingSensor(sensor);
    setLocationFormData({
      location: sensor.location || '',
      location_id: sensor.location_id ?? null,
    });
    setShowLocationModal(true);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSensor(null);
    setError('');
  };

  const handleCloseLocationModal = () => {
    setShowLocationModal(false);
    setEditingSensor(null);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const isDDS45Form = formData.sensor_type.includes('DDS45');

    try {
      if (editingSensor) {
        await sensorService.updateSensor(editingSensor.id, {
          name: formData.name,
          description: formData.description,
          location: formData.location,
          sensor_type: formData.sensor_type,
          ...(formData.sensor_type === 'WSC2-L + PAR' ? {
            par_multiplier: Number(formData.par_multiplier),
          } : {}),
          grower_id: formData.grower_id,
          ...(isDDS45Form ? {
            tank_height_mm: formData.tank_height_mm !== '' ? parseFloat(formData.tank_height_mm) : null,
            sensor_height_mm: formData.sensor_height_mm !== '' ? parseFloat(formData.sensor_height_mm) : null,
            max_fill_height_mm: formData.max_fill_height_mm !== '' ? parseFloat(formData.max_fill_height_mm) : null,
          } : {}),
        });
        await locationService.assignSensorLocation(editingSensor.id, { location_id: formData.location_id });
      } else {
        if (registerWithTTIOnCreate) {
          handleCloseModal();
          handleOpenTTICreateModal();
          return;
        }

        // Create sensor with optional grower_id (null means unassigned)
        const created = await sensorService.createSensor({
          ...formData,
          par_multiplier: Number(formData.par_multiplier),
          grower_id: formData.grower_id,
          tank_height_mm: formData.tank_height_mm !== '' ? parseFloat(formData.tank_height_mm) : null,
          sensor_height_mm: formData.sensor_height_mm !== '' ? parseFloat(formData.sensor_height_mm) : null,
          max_fill_height_mm: formData.max_fill_height_mm !== '' ? parseFloat(formData.max_fill_height_mm) : null,
        });
        if (formData.location_id) {
          await locationService.assignSensorLocation(created.id, { location_id: formData.location_id });
        }
      }
      handleCloseModal();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save sensor');
    }
  };

  const handleLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!editingSensor) return;

    try {
      await sensorService.updateSensorLocation(editingSensor.id, locationFormData.location || null);
      // Also update structured location assignment
      await locationService.assignSensorLocation(editingSensor.id, { location_id: locationFormData.location_id });
      handleCloseLocationModal();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update location');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this sensor? All associated data will be lost.')) {
      return;
    }

    try {
      await sensorService.deleteSensor(id);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete sensor');
    }
  };

  const handleOpenTTIModal = (sensor: Sensor) => {
    setTTISensor(sensor);
    // Pre-select the first verified application key if available
    const verifiedKeys = applicationKeys.filter(k => k.is_verified);
    const firstKey = verifiedKeys[0] ?? null;
    setTTIFormData({
      api_key: '',
      app_id: firstKey ? firstKey.application_id : '',
      region: firstKey ? firstKey.region : 'eu1',
      device_id: sensor.device_id,
      dev_eui: '',
      join_eui: '',
      app_key: '',
      frequency_plan_id: firstKey ? (FREQUENCY_PLAN_BY_REGION[firstKey.region] ?? 'EU_863_870') : 'EU_863_870',
    });
    setTTIError('');
    setTTISuccess('');
    setTTIImageExtractedData(null);
    setShowTTIModal(true);
  };

  const handleOpenTTICreateModal = () => {
    const verifiedKeys = applicationKeys.filter(k => k.is_verified);
    const firstKey = verifiedKeys[0] ?? null;

    setTTISensor(null);
    setTTIFormData({
      api_key: '',
      app_id: firstKey ? firstKey.application_id : '',
      region: firstKey ? firstKey.region : 'eu1',
      device_id: formData.device_id,
      dev_eui: '',
      join_eui: '',
      app_key: '',
      frequency_plan_id: firstKey ? (FREQUENCY_PLAN_BY_REGION[firstKey.region] ?? 'EU_863_870') : 'EU_863_870',
    });
    setTTIError('');
    setTTISuccess('');
    setTTIImageExtractedData(null);
    setShowTTIModal(true);
  };

  const handleCloseTTIModal = () => {
    const shouldReturnToCreateForm = !ttiSensor && registerWithTTIOnCreate;
    setShowTTIModal(false);
    setTTISensor(null);
    setTTIError('');
    setTTISuccess('');
    setTTIImageExtractedData(null);
    if (shouldReturnToCreateForm) {
      setShowModal(true);
    } else {
      setRegisterWithTTIOnCreate(false);
    }
  };

  const handleTTIImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setTTIExtractingImage(true);
    setTTIImageExtractedData(null);
    setTTIError('');

    try {
      const result = await sensorService.extractDeviceDataFromImage(file);
      const normalizedDeviceId = result.device_id
        ? normalizeTTIDeviceId(result.device_id)
        : null;
      setTTIImageExtractedData({ ...result, device_id: normalizedDeviceId });

      if (result.dev_eui) {
        setTTIFormData(prev => ({ ...prev, dev_eui: result.dev_eui || '' }));
      }
      if (result.join_eui) {
        setTTIFormData(prev => ({ ...prev, join_eui: result.join_eui || '' }));
      }
      if (result.app_key) {
        setTTIFormData(prev => ({ ...prev, app_key: result.app_key || '' }));
      }
      // A serial number printed as SN/S/N is the authoritative TTI Device ID.
      if (normalizedDeviceId) {
        setTTIFormData(prev => ({ ...prev, device_id: normalizedDeviceId }));
      }
    } catch (err: any) {
      setTTIError(err.response?.data?.detail || t('sensors.failedToExtractImageData'));
    } finally {
      setTTIExtractingImage(false);
      // Reset the file input so the same file can be re-uploaded
      e.target.value = '';
    }
  };


  const handleTTISubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTTIError('');
    setTTISuccess('');
    setTTILoading(true);

    try {
      let sensorToRegister = ttiSensor;

      if (!sensorToRegister && registerWithTTIOnCreate) {
        const created = await sensorService.createSensor({
          ...formData,
          device_id: ttiFormData.device_id,
          par_multiplier: Number(formData.par_multiplier),
          grower_id: formData.grower_id,
          tank_height_mm: formData.tank_height_mm !== '' ? parseFloat(formData.tank_height_mm) : null,
          sensor_height_mm: formData.sensor_height_mm !== '' ? parseFloat(formData.sensor_height_mm) : null,
          max_fill_height_mm: formData.max_fill_height_mm !== '' ? parseFloat(formData.max_fill_height_mm) : null,
        });
        sensorToRegister = created;
        setTTISensor(created);
        if (formData.location_id) {
          await locationService.assignSensorLocation(created.id, { location_id: formData.location_id });
        }
      }

      if (!sensorToRegister) return;

      const request: TTIRegistrationRequest = {
        api_key: '',
        app_id: ttiFormData.app_id,
        region: ttiFormData.region,
        device_id: ttiFormData.device_id,
        dev_eui: ttiFormData.dev_eui,
        join_eui: ttiFormData.join_eui,
        app_key: ttiFormData.app_key,
        device_name: sensorToRegister.name,
        description: sensorToRegister.description || undefined,
        frequency_plan_id: ttiFormData.frequency_plan_id,
      };

      await sensorService.registerWithTTI(sensorToRegister.id, request);
      setTTISuccess('Successfully registered with TTI!');

      setTimeout(async () => {
        await loadData();
        setShowTTIModal(false);
        setTTISensor(null);
        setTTIError('');
        setTTISuccess('');
        setTTIImageExtractedData(null);
        setRegisterWithTTIOnCreate(false);
      }, 2000);
    } catch (err: any) {
      setTTIError(err.response?.data?.detail || 'Failed to register with TTI');
    } finally {
      setTTILoading(false);
    }
  };

  const handleUnregisterTTI = async (sensor: Sensor) => {
    if (!window.confirm('Are you sure you want to unregister this sensor from TTI? The device will be deleted from The Things Industries platform.')) {
      return;
    }

    try {
      await sensorService.unregisterFromTTI(sensor.id);
      alert('Successfully unregistered from TTI');
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to unregister from TTI');
    }
  };

  if (loading) {
    return (
      <Layout user={user || undefined}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading...</div>
        </div>
      </Layout>
    );
  }

  const isAdmin = user?.is_admin;
  const isAdminGrower = !isAdmin && (user?.is_admin_grower ?? false);
  const isGrower = !isAdmin && !isAdminGrower && user?.grower_ids && user.grower_ids.length > 0;
  const isMultiGrower = !isAdmin && !isAdminGrower && (user?.grower_ids?.length ?? 0) > 1;
  const hasApplicationKeys = applicationKeys.length > 0;

  const getGrowerName = (growerId: number) => {
    const grower = growers.find((g) => g.id === growerId);
    return grower?.name || 'Unknown';
  };

  /** Returns label + whether the sensor sits at the parent (not yet in a sub-grower). */
  const getAssignmentLabel = (growerId: number): { name: string; isParent: boolean } => {
    const grower = growers.find((g) => g.id === growerId);
    if (!grower) return { name: 'Unknown', isParent: false };
    return { name: grower.name, isParent: grower.parent_id === null };
  };

  // New state for controlling the unassigned sensors section collapse

  return (
    <Layout user={user || undefined}>
      <div className="py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">{t('sensors.title')}</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate('/locations')}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              {t('sensors.sensorLocations')}
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={() => navigate('/batch-imports')}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                >
                  {t('nav.batchImports')}
                </button>
                {hasApplicationKeys || (ttiSettings && ttiSettings.is_verified) ? (
                  <button
                    onClick={handleOpenConnectStoredKeyModal}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    {t('sensors.connectTTIDevice')}
                  </button>
                ) : (
                  <button
                    onClick={handleOpenConnectTTIModal}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    {t('sensors.connectTTIDeviceManual')}
                  </button>
                )}
                <button
                  onClick={() => handleOpenModal()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  {t('sensors.addSensor')}
                </button>
              </>
            )}
            {isGrower && (
              <button
                onClick={handleOpenGrowerCreateModal}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                {t('sensors.requestNewSensor')}
              </button>
            )}
          </div>
        </div>

        {/* Pending Sensors Section for Admin */}
        {isAdmin && pendingSensors.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('sensors.pendingApprovalSection', { count: pendingSensors.length })}
            </h3>
            <div className="bg-yellow-50 border border-yellow-200 shadow sm:rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-yellow-200">
                <thead className="bg-yellow-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.name')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('sensors.deviceId')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('sensors.grower')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Device Data</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-yellow-50 divide-y divide-yellow-200">
                  {pendingSensors.map((sensor) => (
                    <tr key={sensor.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sensor.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{sensor.device_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{sensor.grower_id ? getGrowerName(sensor.grower_id) : t('sensors.unassigned')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {sensor.pending_dev_eui && <div>DEV_EUI: {sensor.pending_dev_eui}</div>}
                        {sensor.pending_join_eui && <div>JOIN_EUI: {sensor.pending_join_eui}</div>}
                        {!sensor.pending_dev_eui && !sensor.pending_join_eui && <span className="text-gray-400">{t('sensors.noDeviceData')}</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button onClick={() => handleApproveSensor(sensor.id)} className="text-green-600 hover:text-green-900">{t('sensors.approve')}</button>
                        <button onClick={() => handleRejectSensor(sensor.id)} className="text-red-600 hover:text-red-900">{t('sensors.reject')}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Unassigned Sensors Section for Admin */}
        {isAdmin && unassignedSensors.length > 0 && (
          <div className="mb-8">
            <button
              onClick={() => setUnassignedCollapsed(!unassignedCollapsed)}
              className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4 hover:text-primary-600 focus:outline-none"
            >
              <span>{unassignedCollapsed ? '▶' : '▼'}</span>
              {t('sensors.unassignedSensorsSection', { count: unassignedSensors.length })}
            </button>
            {!unassignedCollapsed && (
              <>
                <p className="text-sm text-gray-600 mb-4">{t('sensors.unassignedHint')}</p>

                {/* Bulk action bar */}
                {selectedUnassignedIds.size > 0 && (
                  <div className="flex items-center gap-3 mb-3 px-4 py-2 bg-blue-100 border border-blue-300 rounded-lg">
                    <span className="text-sm font-medium text-blue-800">
                      {t('sensors.sensorsSelected', { count: selectedUnassignedIds.size })}
                    </span>
                    <button
                      onClick={() => { setBulkAssignGrowerId(growers[0]?.id ?? null); setShowBulkAssignGrowerModal(true); }}
                      className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      {t('sensors.assignToGrower')}
                    </button>
                    <button
                      onClick={() => setSelectedUnassignedIds(new Set())}
                      className="px-3 py-1 text-xs text-blue-700 hover:text-blue-900"
                    >
                      {t('sensors.clearSelection')}
                    </button>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 shadow overflow-hidden sm:rounded-lg">
                  <table className="min-w-full divide-y divide-blue-200">
                    <thead className="bg-blue-100">
                      <tr>
                        <th className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={unassignedSensors.length > 0 && unassignedSensors.every((s) => selectedUnassignedIds.has(s.id))}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedUnassignedIds(new Set(unassignedSensors.map((s) => s.id)));
                              else setSelectedUnassignedIds(new Set());
                            }}
                            className="rounded text-primary-600"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.name')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('sensors.deviceId')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('sensors.allTTIStatus')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.status')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-blue-50 divide-y divide-blue-200">
                      {unassignedSensors.map((sensor) => (
                        <tr key={sensor.id} className={selectedUnassignedIds.has(sensor.id) ? 'bg-blue-100' : ''}>
                          <td className="px-4 py-4">
                            <input type="checkbox" checked={selectedUnassignedIds.has(sensor.id)} onChange={() => toggleUnassignedSelection(sensor.id)} className="rounded text-primary-600" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <Link to={`/sensors/${sensor.id}`} className="text-primary-600 hover:text-primary-700">{sensor.name}</Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{sensor.device_id}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {sensor.tti_registered ? (
                              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">{t('sensors.connected')}</span>
                            ) : (
                              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{t('sensors.notRegistered')}</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${sensor.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {sensor.is_active ? t('common.active') : t('common.inactive')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <button onClick={() => handleOpenAssignGrowerModal(sensor)} className="text-blue-600 hover:text-blue-900">{t('sensors.assignToGrower')}</button>
                            <button onClick={() => handleOpenModal(sensor)} className="text-gray-600 hover:text-gray-900">{t('common.edit')}</button>
                            <button onClick={() => handleDelete(sensor.id)} className="text-red-600 hover:text-red-900">{t('common.delete')}</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {sensors.length === 0 && unassignedSensors.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-500">
              {isAdmin
                ? t('sensors.noSensorsAdmin')
                : isGrower
                ? t('sensors.noSensorsAssigned')
                : t('sensors.noSensors')}
            </p>
          </div>
        ) : (
          <>
            {/* Search and Filter Section */}
            <div className="bg-white shadow rounded-lg p-4 mb-4">
              <div className="flex flex-col gap-4">
                {/* Search Bar */}
                <div className="flex gap-2 items-center">
                  <div className="flex-1">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder={t('sensors.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setSensorPage(1); }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <svg
                        className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  {(searchTerm || filterApplication !== 'all' || filterGrower !== 'all' || filterStatus !== 'all' || filterTTIStatus !== 'all' || filterRegion !== 'all' || filterSensorType !== 'all' || filterGateway !== 'all') && (
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {t('common.clearAll')}
                    </button>
                  )}
                </div>

                {/* Filter Dropdowns */}
                <div className="flex flex-wrap gap-2">
                  {/* Application Filter - Admin Only */}
                  {isAdmin && uniqueApplications.length > 0 && (
                    <select
                      value={filterApplication}
                      onChange={(e) => { setFilterApplication(e.target.value); setSensorPage(1); }}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">{t('common.all')} {t('sensors.applications')}</option>
                      <option value="unassigned">{t('sensors.unassigned')}</option>
                      {uniqueApplications.map(app => (
                        <option key={app} value={app}>{app}</option>
                      ))}
                    </select>
                  )}

                  {/* Grower Filter - Admin and multi-grower users */}
                  {(isAdmin || isMultiGrower) && growers.length > 0 && (
                    <select
                      value={filterGrower}
                      onChange={(e) => { setFilterGrower(e.target.value); setSensorPage(1); }}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">{t('common.all')} {t('sensors.growers')}</option>
                      {isAdmin && <option value="unassigned">{t('sensors.unassigned')}</option>}
                      {growers.map(grower => (
                        <option key={grower.id} value={grower.id.toString()}>{grower.name}</option>
                      ))}
                    </select>
                  )}

                  {/* Status Filter */}
                  <select
                    value={filterStatus}
                    onChange={(e) => { setFilterStatus(e.target.value); setSensorPage(1); }}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">{t('common.all')} {t('common.status')}</option>
                    <option value="active">{t('common.active')}</option>
                    <option value="inactive">{t('common.inactive')}</option>
                  </select>

                  {/* TTI Status Filter */}
                  <select
                    value={filterTTIStatus}
                    onChange={(e) => { setFilterTTIStatus(e.target.value); setSensorPage(1); }}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">{t('common.all')} {t('sensors.ttiStatus')}</option>
                    <option value="registered">{t('sensors.ttiRegistered')}</option>
                    <option value="not_registered">{t('sensors.ttiNotRegistered')}</option>
                  </select>

                  {/* Region Filter - shown when there are sensors with regions */}
                  {uniqueRegions.length > 0 && (
                    <select
                      value={filterRegion}
                      onChange={(e) => { setFilterRegion(e.target.value); setSensorPage(1); }}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">{t('common.all')} {t('sensors.regions')}</option>
                      <option value="unassigned">{t('sensors.noRegion')}</option>
                      {uniqueRegions.map(region => (
                        <option key={region} value={region}>{region.toUpperCase()}</option>
                      ))}
                    </select>
                  )}

                  {/* Sensor Type Filter - shown when there are multiple sensor types */}
                  {uniqueSensorTypes.length > 0 && (
                    <select
                      value={filterSensorType}
                      onChange={(e) => { setFilterSensorType(e.target.value); setSensorPage(1); }}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">{t('common.all')} {t('sensors.sensorTypes')}</option>
                      {uniqueSensorTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  )}

                  {/* Gateway Filter - Admin Only */}
                  {isAdmin && (
                    <select
                      value={filterGateway}
                      onChange={(e) => { setFilterGateway(e.target.value); setSensorPage(1); }}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">{t('gateways.allGateways')}</option>
                      <option value="unassigned">{t('sensors.noGateway')}</option>
                      {uniqueGateways.map(([gatewayId, displayName]) => (
                        <option key={gatewayId} value={gatewayId}>
                          {displayName === gatewayId ? gatewayId : `${displayName} (${gatewayId})`}
                        </option>
                      ))}
                    </select>
                  )}

                    {/* Results Count */}
                  <div className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600">
                    <span>
                      {filteredSensors.length < sensors.length
                        ? `${filteredSensors.length} ${t('common.filtered')}`
                        : `${sensors.length} sensor${sensors.length !== 1 ? 's' : ''}`}
                      {filteredSensors.length > sensorsPerPage && (
                        <span className="text-gray-400"> — page {sensorPage} of {sensorTotalPages}</span>
                      )}
                    </span>
                    {lastUsedFilterName && (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                        </svg>
                        {t('sensors.selectedFilter')}: <strong className="ml-0.5">{lastUsedFilterName}</strong>
                      </span>
                    )}
                  </div>

                  {/* Save Filter Button */}
                  <button
                    onClick={openSaveFilterModal}
                    className="px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-1"
                    title={t('sensors.saveFilterTooltip')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    {t('dashboard.saveFilter')}
                  </button>

                  {/* Load Saved Filter Dropdown */}
                  {savedFilters.length > 0 && (
                    <div className="relative group">
                      <button
                        className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1"
                        title={t('sensors.loadFilterTooltip')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                        </svg>
                        {t('sensors.savedFilters')}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block group-focus-within:block">
                        {savedFilters.map(filter => (
                          <div key={filter.name} className={`flex items-center justify-between px-3 py-2 hover:bg-gray-50 group/item ${filter.name === lastUsedFilterName ? 'bg-primary-50' : ''}`}>
                            <button
                              onClick={() => handleLoadFilter(filter)}
                              className={`text-sm flex-1 text-left truncate ${filter.name === lastUsedFilterName ? 'text-primary-700 font-medium' : 'text-gray-700'}`}
                              title={filter.name}
                            >
                              {filter.name === lastUsedFilterName && <span className="mr-1">✓</span>}
                              {filter.name}
                            </button>
                            <button
                              onClick={() => handleDeleteSavedFilter(filter.name)}
                              className="ml-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                              title={t('common.delete')}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Save Filter Modal */}
            {showSaveFilterModal && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{t('sensors.saveFilter')}</h3>

                  {/* Mode toggle — only shown when a filter is currently active */}
                  {lastUsedFilterName && (
                    <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-4">
                      <button
                        type="button"
                        onClick={() => setSaveFilterMode('update')}
                        className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${saveFilterMode === 'update' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      >
                        {t('sensors.updateCurrent')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSaveFilterMode('new')}
                        className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${saveFilterMode === 'new' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      >
                        {t('sensors.saveAsNew')}
                      </button>
                    </div>
                  )}

                  {/* Update mode — just confirms the active filter name */}
                  {saveFilterMode === 'update' && lastUsedFilterName ? (
                    <p className="text-sm text-gray-600 mb-4">
                      {t('sensors.filterWillBeOverwritten', { filterName: lastUsedFilterName })}
                    </p>
                  ) : (
                    /* New mode — asks for a name */
                    <input
                      type="text"
                      autoFocus
                      placeholder={t('dashboard.enterFilterName')}
                      value={filterNameInput}
                      onChange={e => setFilterNameInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveFilter(); if (e.key === 'Escape') setShowSaveFilterModal(false); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 mb-4"
                    />
                  )}

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowSaveFilterModal(false)}
                      className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleSaveFilter}
                      disabled={saveFilterMode === 'new' && !filterNameInput.trim()}
                      className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saveFilterMode === 'update' && lastUsedFilterName ? t('common.update') : t('common.save')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sensor Table or No Results */}
            {filteredSensors.length === 0 ? (
              <div className="bg-white shadow rounded-lg p-6 text-center">
                <p className="text-gray-500 mb-2">{t('sensors.noSensorsMatch')}</p>
                <button
                  onClick={clearFilters}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  {t('sensors.clearFilters')}
                </button>
              </div>
            ) : (
              <>
              {/* Top pagination controls */}
              {sensorTotalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 sm:rounded-t-lg mb-0">
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <span>
                      {t('sensors.showing')}{' '}
                      <span className="font-medium">{(sensorPage - 1) * sensorsPerPage + 1}</span>
                      {' '}–{' '}
                      <span className="font-medium">{Math.min(sensorPage * sensorsPerPage, filteredSensors.length)}</span>
                      {' '}of{' '}
                      <span className="font-medium">{filteredSensors.length}</span> {t('sensors.sensors')}
                    </span>
                    <label className="flex items-center gap-1.5">
                      {t('sensors.perPage')}:
                      <select
                        value={sensorsPerPage}
                        onChange={e => { setSensorsPerPage(Number(e.target.value)); setSensorPage(1); }}
                        className="border border-gray-300 rounded-md text-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </label>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setSensorPage(1)}
                      disabled={sensorPage === 1}
                      className="px-2 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                      title={t('common.firstPage')}
                    >«</button>
                    <button
                      onClick={() => setSensorPage(p => Math.max(1, p - 1))}
                      disabled={sensorPage === 1}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                    >{t('common.previous')}</button>
                    {Array.from({ length: sensorTotalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === sensorTotalPages || Math.abs(p - sensorPage) <= 2)
                      .reduce<(number | 'gap')[]>((acc, p, idx, arr) => {
                        if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('gap');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, idx) =>
                        p === 'gap' ? (
                          <span key={`gap-top-${idx}`} className="px-2 py-1.5 text-sm text-gray-400">…</span>
                        ) : (
                          <button
                            key={`top-${p}`}
                            onClick={() => setSensorPage(p as number)}
                            className={`px-3 py-1.5 text-sm border rounded-md ${
                              sensorPage === p
                                ? 'bg-primary-600 text-white border-primary-600 font-medium'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >{p}</button>
                        )
                      )}
                    <button
                      onClick={() => setSensorPage(p => Math.min(sensorTotalPages, p + 1))}
                      disabled={sensorPage === sensorTotalPages}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                    >{t('common.next')}</button>
                    <button
                      onClick={() => setSensorPage(sensorTotalPages)}
                      disabled={sensorPage === sensorTotalPages}
                      className="px-2 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                      title={t('common.lastPage')}
                    >»</button>
                  </div>
                </div>
              )}
              <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.name')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sensors.deviceId')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sensors.sensorType')}
                  </th>
                  {isAdmin && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('sensors.grower')}
                    </th>
                  )}
                  {isAdminGrower && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('sensors.assignedTo')}
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sensors.location')}
                  </th>
                  {isAdmin && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('sensors.gateway')}
                    </th>
                  )}
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.status')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sensors.tti')}
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedSensors.map((sensor) => (
                  <tr key={sensor.id}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link
                        to={`/sensors/${sensor.id}`}
                        className="text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        {sensor.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {sensor.device_id}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-800">
                        {sensor.sensor_type}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {sensor.grower_id ? (
                          <span className="flex items-center">
                            {getGrowerName(sensor.grower_id)}
                            <button
                              onClick={() => handleOpenAssignGrowerModal(sensor)}
                              className="ml-2 text-blue-600 hover:text-blue-900 text-xs"
                              title={t('sensors.changeGrowerAssignment')}
                            >
                              ✎
                            </button>
                          </span>
                        ) : (
                          <span className="text-orange-600 italic">{t('sensors.unassigned')}</span>
                        )}
                      </td>
                    )}
                    {isAdminGrower && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {sensor.grower_id ? (() => {
                          const { name, isParent } = getAssignmentLabel(sensor.grower_id);
                          return isParent ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-700">
                                {t('sensors.notInSubGrower')}
                              </span>
                              <span className="text-gray-400 text-xs">{name}</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <span className="px-1.5 py-0.5 text-xs rounded bg-green-100 text-green-700">
                                {name}
                              </span>
                            </span>
                          );
                        })() : (
                          <span className="text-red-500 italic text-xs">{t('sensors.unassigned')}</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {sensor.location_id
                        ? (() => {
                            const loc = locations.find((l) => l.id === sensor.location_id);
                            return loc ? (
                              <span className="inline-flex items-center gap-1">
                                <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">{loc.name}</span>
                                {sensor.location && <span className="text-gray-400 text-xs">{sensor.location}</span>}
                              </span>
                            ) : (sensor.location || '-');
                          })()
                        : (sensor.location || '-')}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm">
                        {sensor.last_seen_gateway_id ? (
                          <Link
                            to={`/gateways/${encodeURIComponent(sensor.last_seen_gateway_id)}`}
                            className="block max-w-[180px] truncate font-mono text-xs font-medium text-primary-600 hover:text-primary-800"
                            title={sensor.last_seen_gateway_id}
                          >
                            {sensor.last_seen_gateway_id}
                          </Link>
                        ) : (
                          <span className="text-xs text-gray-400">{t('sensors.noGateway')}</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-3 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                          sensor.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                        title={sensor.is_active ? t('common.active') : t('common.inactive')}
                      >
                        {sensor.is_active ? '●' : '○'}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {sensor.tti_registered ? (
                        <div className="flex items-center justify-center gap-1">
                          <span 
                            className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800"
                            title={t('sensors.registeredOnTTI')}
                          >
                            ✓
                          </span>
                          {sensor.tti_activated !== null && (
                            <span 
                              className={`px-2 py-1 text-xs rounded-full ${
                                sensor.tti_activated 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-orange-100 text-orange-800'
                              }`}
                              title={sensor.tti_activated ? t('sensors.ttiActive') : t('sensors.ttiDisabled')}
                            >
                              {sensor.tti_activated ? '●' : '○'}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <span 
                            className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800"
                            title={t('sensors.notRegisteredOnTTI')}
                          >
                            �✗
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenModal(sensor)}
                          className="text-blue-600 hover:text-blue-900 hover:bg-blue-50 p-1 rounded transition-colors"
                          title={t('common.edit')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(sensor.id)}
                            className="text-red-600 hover:text-red-900 hover:bg-red-50 p-1 rounded transition-colors"
                            title={t('common.delete')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

            {/* Pagination controls */}
            {sensorTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:rounded-b-lg">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span>
                    {t('sensors.showing')}{' '}
                    <span className="font-medium">{(sensorPage - 1) * sensorsPerPage + 1}</span>
                    {' '}–{' '}
                    <span className="font-medium">{Math.min(sensorPage * sensorsPerPage, filteredSensors.length)}</span>
                    {' '}of{' '}
                    <span className="font-medium">{filteredSensors.length}</span> {t('sensors.sensors')}
                  </span>
                  <label className="flex items-center gap-1.5">
                    {t('sensors.perPage')}:
                    <select
                      value={sensorsPerPage}
                      onChange={e => { setSensorsPerPage(Number(e.target.value)); setSensorPage(1); }}
                      className="border border-gray-300 rounded-md text-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </label>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setSensorPage(1)}
                    disabled={sensorPage === 1}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                    title={t('common.firstPage')}
                  >«</button>
                  <button
                    onClick={() => setSensorPage(p => Math.max(1, p - 1))}
                    disabled={sensorPage === 1}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                  >{t('common.previous')}</button>
                  {Array.from({ length: sensorTotalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === sensorTotalPages || Math.abs(p - sensorPage) <= 2)
                    .reduce<(number | 'gap')[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('gap');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === 'gap' ? (
                        <span key={`gap-${idx}`} className="px-2 py-1.5 text-sm text-gray-400">…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setSensorPage(p as number)}
                          className={`px-3 py-1.5 text-sm border rounded-md ${
                            sensorPage === p
                              ? 'bg-primary-600 text-white border-primary-600 font-medium'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >{p}</button>
                      )
                    )}

                  <button
                    onClick={() => setSensorPage(p => Math.min(sensorTotalPages, p + 1))}
                    disabled={sensorPage === sensorTotalPages}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                  >{t('common.next')}</button>
                  <button
                    onClick={() => setSensorPage(sensorTotalPages)}
                    disabled={sensorPage === sensorTotalPages}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed"
                    title={t('common.lastPage')}
                  >»</button>
                </div>
              </div>
            )}
              </>
            )}
          </>
        )}

        {/* Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSensor ? t('sensors.editSensor') : t('sensors.addSensor')}
              </h3>
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}
              {!isAdmin && editingSensor && (
                <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded text-sm">
                  <p className="font-medium mb-1">{t('sensors.limitedEditAccess')}</p>
                  <p>{t('sensors.limitedEditDescription')}</p>
                </div>
              )}
              <form onSubmit={handleSubmit}>
                {!editingSensor && isAdmin && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('sensors.deviceId')} *
                    </label>
                    <input
                      type="text"
                      required={!registerWithTTIOnCreate}
                      value={formData.device_id}
                      onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., lbse01-001"
                    />
                  </div>
                )}
                {editingSensor && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('sensors.deviceId')}
                    </label>
                    <input
                      type="text"
                      disabled
                      value={formData.device_id}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('sensors.deviceIdCannotBeChanged')}</p>
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.name')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                {isAdmin && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('sensors.sensorType')}
                    </label>
                    <select
                      value={formData.sensor_type}
                      onChange={(e) => setFormData({ ...formData, sensor_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="LBSE-01">LBSE-01 (Soil)</option>
                      <option value="SE01-LB">SE01-LB (Soil)</option>
                      <option value="LMS01">LMS01 (Leaf)</option>
                      <option value="SE0X-LB">SE0X-LB (Multi-probe)</option>
                      <option value="DDS45-LB">DDS45-LB (Distance/Level)</option>
                      <option value="LHT65N">LHT65N (Temperature &amp; Humidity)</option>
                      <option value="WQS-LB + DR-PH01">{t('wqsPh.sensorLabel')}</option>
                      <option value="WQS-LB + DR-ECK1.0">{t('wqsEc.sensorLabel')}</option>
                      <option value="WSC2-L + PAR">{t('par.sensorLabel')}</option>
                    </select>
                  </div>
                )}
                {isAdmin && formData.sensor_type === 'WSC2-L + PAR' && (
                  <div className="mb-4">
                    <label htmlFor="par-multiplier" className="block text-sm font-medium text-gray-700 mb-2">{t('par.multiplier')}</label>
                    <input id="par-multiplier" type="number" min="0.001" step="any" required
                      value={formData.par_multiplier}
                      onChange={e => setFormData({ ...formData, par_multiplier: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500" />
                    <p className="text-xs text-gray-500 mt-1">{t('par.multiplierHint')}</p>
                  </div>
                )}
                {isAdmin && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('sensors.assignToGrower')}
                    </label>
                    <select
                      value={formData.grower_id ?? ''}
                      onChange={(e) =>
                        setFormData({ ...formData, grower_id: e.target.value ? parseInt(e.target.value) : null })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">{t('sensors.noGrower')} ({t('common.unassigned')})</option>
                      {growers.map((grower) => (
                        <option key={grower.id} value={grower.id}>
                          {grower.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {editingSensor ? t('sensors.changeOrRemoveGrower') : t('sensors.assignLater')}
                    </p>
                  </div>
                )}
                {isAdmin && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('common.description')}
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('sensors.location')}
                  </label>
                  <select
                    value={formData.location_id ?? ''}
                    onChange={(e) => setFormData({ ...formData, location_id: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">{t('sensors.noLocation')}</option>
                    {locations
                      .filter((loc) => !formData.grower_id || loc.grower_id === formData.grower_id)
                      .map((loc) => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                  </select>
                  {locations.filter((loc) => !formData.grower_id || loc.grower_id === formData.grower_id).length === 0 && (
                    <p className="mt-1 text-xs text-gray-500">{t('sensors.noLocationsDefined')}</p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('sensors.locationNote')} <span className="text-gray-400 font-normal">({t('common.freeText')})</span>
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('sensors.locationPlaceholder')}
                  />
                </div>

                {/* DDS45-LB Tank Configuration - only shown for DDS45 sensors */}
                {isAdmin && formData.sensor_type.includes('DDS45') && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">{t('sensors.dds45Settings')}</h4>
                    <p className="text-xs text-gray-500 mb-3">{t('sensors.dds45SettingsDescription')}</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('sensors.tankHeight')} (mm)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.tank_height_mm}
                          onChange={(e) => setFormData({ ...formData, tank_height_mm: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g. 3500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('sensors.sensorHeight')} (mm)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.sensor_height_mm}
                          onChange={(e) => setFormData({ ...formData, sensor_height_mm: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g. 3500"
                        />
                        <p className="text-xs text-gray-400 mt-1">{t('sensors.sensorHeightHint')}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('sensors.maxFillHeight')} (mm)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.max_fill_height_mm}
                          onChange={(e) => setFormData({ ...formData, max_fill_height_mm: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g. 3000"
                        />
                        <p className="text-xs text-gray-400 mt-1">{t('sensors.maxFillHeightHint')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* TTI Registration Option - Only show when creating new sensor and user is admin */}
                {isAdmin && !editingSensor && (
                  <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="registerWithTTI"
                        checked={registerWithTTIOnCreate}
                        onChange={(e) => setRegisterWithTTIOnCreate(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="registerWithTTI" className="ml-2 text-sm font-medium text-gray-700">
                        {t('sensors.registerWithTTIImmediately')}
                      </label>
                    </div>
                    {registerWithTTIOnCreate && (
                      <p className="mt-2 text-xs text-green-700">
                        {t('sensors.ttiRegistrationAfterSave')}
                      </p>
                    )}
                  </div>
                )}

                {/* TTI Management Section - Only show when editing and user is admin */}
                {isAdmin && editingSensor && editingSensor.tti_registered && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">{t('sensors.ttiIntegration')}</h4>
                    <div className="text-sm text-gray-600 mb-3">
                      <div className="flex items-center mb-1">
                        <span className="w-24">{t('common.status')}:</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          editingSensor.tti_activated 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {editingSensor.tti_activated ? t('sensors.ttiActive') : t('sensors.ttiDisabled')}
                        </span>
                      </div>
                      <div className="flex items-center mb-1">
                        <span className="w-24">{t('sensors.deviceId')}:</span>
                        <span className="font-mono text-xs">{editingSensor.tti_device_id}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-24">{t('sensors.application')}:</span>
                        <span className="font-mono text-xs">{editingSensor.tti_app_id}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {editingSensor.tti_activated === false ? (
                        <button
                          type="button"
                          onClick={() => {
                            handleCloseModal();
                            handleTTIActivation(editingSensor, true);
                          }}
                          className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          {t('sensors.enableTTI')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            handleCloseModal();
                            handleTTIActivation(editingSensor, false);
                          }}
                          className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
                        >
                          {t('sensors.disableTTI')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          handleCloseModal();
                          handleUnregisterTTI(editingSensor);
                        }}
                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        {t('sensors.unregisterFromTTI')}
                      </button>
                    </div>
                  </div>
                )}

                {/* TTI Registration Button - Only show when not registered and user is admin */}
                {isAdmin && editingSensor && !editingSensor.tti_registered && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-800 mb-2">{t('sensors.ttiIntegration')}</h4>
                    <p className="text-xs text-gray-600 mb-3">
                      {t('sensors.notRegisteredWithTTI')}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        handleCloseModal();
                        handleOpenTTIModal(editingSensor);
                      }}
                      className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      {t('sensors.registerWithTTI')}
                    </button>
                  </div>
                )}

                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    {editingSensor
                      ? t('common.update')
                      : registerWithTTIOnCreate
                        ? t('common.next')
                        : t('common.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* TTI Registration Modal */}
        {showTTIModal && (ttiSensor || registerWithTTIOnCreate) && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white mb-10">

              {/* Header */}
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('sensors.registerWithTTI')}
                </h3>
                <button type="button" onClick={handleCloseTTIModal} className="text-gray-400 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {t('sensors.registerSensor', { sensorName: ttiSensor?.name || formData.name })}
              </p>

              {ttiError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                  {ttiError}
                </div>
              )}

              {ttiSuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-sm">
                  {ttiSuccess}
                </div>
              )}

              <form onSubmit={handleTTISubmit}>

                {/* ── Step 1: Select Application ── */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-bold">1</span>
                    {t('sensors.ttiSelectApplication')}
                  </h4>

                  {applicationKeys.filter(k => k.is_verified).length === 0 ? (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 mb-3">
                      {t('sensors.noVerifiedApps')}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      {applicationKeys.filter(k => k.is_verified).map(appKey => (
                          <button
                            key={appKey.id}
                            type="button"
                            onClick={() => {
                              setTTIFormData(prev => ({
                                ...prev,
                                app_id: appKey.application_id,
                                region: appKey.region,
                                frequency_plan_id: FREQUENCY_PLAN_BY_REGION[appKey.region] ?? prev.frequency_plan_id,
                              }));
                            }}
                            className={`p-3 border-2 rounded-lg text-left transition-colors ${
                              ttiFormData.app_id === appKey.application_id
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-400 bg-white'
                            }`}
                          >
                            <div className="font-medium text-gray-900 text-sm">{appKey.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{appKey.application_id}</div>
                            <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-x-3">
                              <span>{t('sensors.cloudRegion')}: {appKey.region.toUpperCase()}</span>
                              <span>{t('sensors.frequencyPlan')}: {getFrequencyPlanLabel(FREQUENCY_PLAN_BY_REGION[appKey.region] ?? '-')}</span>
                            </div>
                          </button>
                      ))}
                    </div>
                  )}

                </div>

                {/* ── Step 2: Region & Frequency Plan ── */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-bold">2</span>
                    {t('sensors.ttiRegionFrequency')}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('sensors.cloudRegion')} *
                      </label>
                      <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-sm text-gray-700">
                        {ttiFormData.app_id ? ttiFormData.region.toUpperCase() : '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('sensors.frequencyPlan')} *
                      </label>
                      <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-sm text-gray-700">
                        {ttiFormData.app_id ? getFrequencyPlanLabel(ttiFormData.frequency_plan_id) : '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Step 3: Device Credentials ── */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-bold">3</span>
                    {t('sensors.ttiDeviceCredentials')}
                  </h4>

                  {/* Image upload */}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">{t('sensors.ttiUploadImageTitle')}</p>
                    <p className="text-xs text-blue-600 mb-3">{t('sensors.ttiUploadImageHint')}</p>
                    <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                      ttiExtractingImage
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}>
                      {ttiExtractingImage ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('sensors.extractingImageData')}
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {t('sensors.uploadSensorImage')}
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        disabled={ttiExtractingImage}
                        onChange={handleTTIImageUpload}
                      />
                    </label>

                    {ttiImageExtractedData && (
                      <div className="mt-3 p-2 bg-white border border-blue-200 rounded text-xs text-gray-600">
                        <span className="font-medium text-green-700">{t('sensors.imageDataExtracted')}</span>
                        {' '}
                        {t('sensors.confidenceScore', { score: Math.round(ttiImageExtractedData.confidence * 100) })}
                        <dl className="mt-2 space-y-1 font-mono text-gray-700">
                          {ttiImageExtractedData.device_id && (
                            <div>
                              <dt className="inline font-sans font-medium">{t('sensors.deviceId')}:</dt>{' '}
                              <dd className="inline break-all">{ttiImageExtractedData.device_id}</dd>
                            </div>
                          )}
                          {ttiImageExtractedData.dev_eui && (
                            <div>
                              <dt className="inline font-sans font-medium">DEV_EUI:</dt>{' '}
                              <dd className="inline break-all">{ttiImageExtractedData.dev_eui}</dd>
                            </div>
                          )}
                          {ttiImageExtractedData.join_eui && (
                            <div>
                              <dt className="inline font-sans font-medium">APP_EUI:</dt>{' '}
                              <dd className="inline break-all">{ttiImageExtractedData.join_eui}</dd>
                            </div>
                          )}
                          {ttiImageExtractedData.app_key && (
                            <div>
                              <dt className="inline font-sans font-medium">APP_KEY:</dt>{' '}
                              <dd className="inline break-all">{ttiImageExtractedData.app_key}</dd>
                            </div>
                          )}
                        </dl>
                        {ttiImageExtractedData.raw_text && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-gray-400">{t('sensors.rawTextFromImage')}</summary>
                            <pre className="mt-1 whitespace-pre-wrap break-all">{ttiImageExtractedData.raw_text}</pre>
                          </details>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Device ID */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('sensors.deviceId')} *
                    </label>
                    <input
                      type="text"
                      required
                      value={ttiFormData.device_id}
                      onChange={(e) => setTTIFormData({ ...ttiFormData, device_id: e.target.value.toLowerCase() })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                      placeholder={t('sensors.uniqueDeviceId')}
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('sensors.deviceIdMustBeUnique')}</p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('sensors.deviceEui')} (DEV_EUI) *
                      </label>
                      <input
                        type="text"
                        required
                        value={ttiFormData.dev_eui}
                        onChange={(e) => setTTIFormData({ ...ttiFormData, dev_eui: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        placeholder="0000000000000000"
                        pattern="[0-9A-Fa-f]{16}"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('sensors.hexCharacters16')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('sensors.joinEui')} (APP_EUI) *
                      </label>
                      <input
                        type="text"
                        required
                        value={ttiFormData.join_eui}
                        onChange={(e) => setTTIFormData({ ...ttiFormData, join_eui: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        placeholder="0000000000000000"
                        pattern="[0-9A-Fa-f]{16}"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('sensors.hexCharacters16')}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('sensors.appKey')} (APP_KEY) *
                    </label>
                    <input
                      type="text"
                      required
                      value={ttiFormData.app_key}
                      onChange={(e) => setTTIFormData({ ...ttiFormData, app_key: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                      placeholder="00000000000000000000000000000000"
                      pattern="[0-9A-Fa-f]{32}"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('sensors.hexCharacters32')}</p>
                  </div>
                </div>

                <div className="flex space-x-2 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseTTIModal}
                    disabled={ttiLoading}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={ttiLoading || !ttiFormData.app_id}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {ttiLoading ? t('sensors.registering') : t('sensors.registerWithTTI')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Connect TTI Device (Stored Application Key) Modal */}
        {showConnectStoredKeyModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-10">

              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('sensors.connectTTIDevice')}</h3>
                <button type="button" onClick={handleCloseConnectStoredKeyModal} className="text-gray-400 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {connectStoredKeyError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                  {connectStoredKeyError}
                </div>
              )}

              <form onSubmit={handleConnectStoredKeySubmit}>

                {/* ── Step 1: Select Application ── */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-bold">1</span>
                    {t('sensors.selectApplication')}
                  </h4>
                  {applicationKeys.filter(k => k.is_verified).length === 0 ? (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                      {t('sensors.noVerifiedApps')}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {applicationKeys.filter(k => k.is_verified).map(appKey => (
                        <button
                          key={appKey.id}
                          type="button"
                          onClick={() => {
                            setConnectStoredKeyFormData(prev => ({ ...prev, app_id: appKey.application_id }));
                            handleLoadTTIDevicesFromAppKey(appKey.id);
                            setSelectedDeviceIds(new Set());
                            setConnectResults([]);
                          }}
                          className={`p-3 border-2 rounded-lg text-left transition-colors ${
                            connectStoredKeyFormData.app_id === appKey.application_id
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-400 bg-white'
                          }`}
                        >
                          <div className="font-medium text-gray-900 text-sm">{appKey.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{appKey.application_id}</div>
                          <div className="text-xs text-gray-400">{appKey.region.toUpperCase()}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Step 2: Select Devices ── */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-bold">2</span>
                      {t('sensors.loadDevices')}
                    </h4>
                    {ttiDevices.length > 0 && (
                      <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={hideConnectedDevices}
                          onChange={e => setHideConnectedDevices(e.target.checked)}
                          className="rounded"
                        />
                        {t('sensors.hideConnectedDevices')}
                      </label>
                    )}
                  </div>

                  {!connectStoredKeyFormData.app_id ? (
                    <p className="text-sm text-gray-400 italic py-2">{t('sensors.selectAppFirst')}</p>
                  ) : loadingTTIDevices ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent" />
                      {t('common.loading')}
                    </div>
                  ) : ttiDevices.length === 0 ? (
                    <p className="text-sm text-gray-500 italic py-2">{t('sensors.noDeviceData')}</p>
                  ) : (() => {
                    const available = ttiDevices.filter(d => !d.is_connected);
                    const displayed = hideConnectedDevices ? available : ttiDevices;
                    const allSelected = available.length > 0 && available.every(d => selectedDeviceIds.has(d.device_id));
                    return (
                      <div>
                        {/* Select-all bar */}
                        <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-t-lg">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            disabled={available.length === 0}
                            onChange={e => {
                              if (e.target.checked) setSelectedDeviceIds(new Set(available.map(d => d.device_id)));
                              else setSelectedDeviceIds(new Set());
                            }}
                            className="rounded text-primary-600"
                          />
                          <span className="text-xs text-gray-600">
                            {t('sensors.availableDevices', { count: available.length })}
                            {selectedDeviceIds.size > 0 && (
                              <span className="ml-2 font-medium text-primary-700">
                                · {t('sensors.sensorsSelected', { count: selectedDeviceIds.size })}
                              </span>
                            )}
                          </span>
                        </div>
                        {/* Device list */}
                        <div className="max-h-52 overflow-y-auto border border-t-0 border-gray-200 rounded-b-lg divide-y divide-gray-100">
                          {displayed.map(device => (
                            <label
                              key={device.device_id}
                              className={`flex items-center gap-3 px-3 py-2.5 ${
                                device.is_connected
                                  ? 'opacity-50 cursor-not-allowed bg-gray-50'
                                  : selectedDeviceIds.has(device.device_id)
                                  ? 'bg-primary-50 cursor-pointer'
                                  : 'hover:bg-gray-50 cursor-pointer'
                              }`}
                            >
                              <input
                                type="checkbox"
                                disabled={device.is_connected}
                                checked={selectedDeviceIds.has(device.device_id)}
                                onChange={() => {
                                  if (device.is_connected) return;
                                  setSelectedDeviceIds(prev => {
                                    const next = new Set(prev);
                                    next.has(device.device_id) ? next.delete(device.device_id) : next.add(device.device_id);
                                    return next;
                                  });
                                }}
                                className="rounded text-primary-600 flex-shrink-0"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-gray-900 truncate">
                                    {device.name || device.device_id}
                                  </span>
                                  {device.is_connected && (
                                    <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded flex-shrink-0">
                                      {t('sensors.alreadyConnected')}
                                    </span>
                                  )}
                                </div>
                                {device.name && device.name !== device.device_id && (
                                  <div className="text-xs text-gray-400">{device.device_id}</div>
                                )}
                                {device.dev_eui && (
                                  <div className="text-xs text-gray-400">EUI: {device.dev_eui}</div>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* ── Step 3: Shared Settings ── */}
                {selectedDeviceIds.size > 0 && (
                  <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-bold">3</span>
                      {t('sensors.connectSettings')}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('sensors.sensorType')}</label>
                        <select
                          value={sharedSensorType}
                          onChange={e => setSharedSensorType(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        >
                          <option value="LBSE-01">LBSE-01 (Soil)</option>
                          <option value="SE01-LB">SE01-LB (Soil)</option>
                          <option value="LMS01">LMS01 (Leaf)</option>
                          <option value="SE0X-LB">SE0X-LB (Multi-probe)</option>
                          <option value="DDS45-LB">DDS45-LB (Distance/Level)</option>
                          <option value="LHT65N">LHT65N (Temperature &amp; Humidity)</option>
                          <option value="WQS-LB + DR-PH01">{t('wqsPh.sensorLabel')}</option>
                          <option value="WQS-LB + DR-ECK1.0">{t('wqsEc.sensorLabel')}</option>
                          <option value="WSC2-L + PAR">{t('par.sensorLabel')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('sensors.assignToGrower')}</label>
                        <select
                          value={sharedGrowerId ?? ''}
                          onChange={e => setSharedGrowerId(e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        >
                          <option value="">{t('sensors.noGrower')} ({t('common.unassigned')})</option>
                          {growers.map(g => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Connection Results ── */}
                {connectResults.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('sensors.connectionResults')}</h4>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {connectResults.map((r, i) => (
                        <div key={i} className={`flex items-center justify-between gap-3 px-3 py-2 text-xs ${r.success ? 'bg-green-50' : 'bg-red-50'}`}>
                          <span className="font-mono truncate flex-1">{r.device_id}</span>
                          <span className={`px-2 py-0.5 rounded flex-shrink-0 ${r.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {r.success ? t('common.success') : t('common.failed')}
                          </span>
                          <span className="text-gray-500 truncate max-w-[160px]">{r.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Actions ── */}
                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseConnectStoredKeyModal}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={connectStoredKeyLoading || selectedDeviceIds.size === 0}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    {connectStoredKeyLoading
                      ? t('sensors.connecting')
                      : t('sensors.connectCount', { count: selectedDeviceIds.size })}
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}

        {/* CSV Batch Import Modal */}
        {showCSVImportModal && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('sensors.csvBatchImport')} - {t('sensors.registerDevicesWithTTI')}
              </h3>
              
              {/* Column Info Section - Collapsible */}
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => setCsvColumnsExpanded(!csvColumnsExpanded)}
                  className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 mb-2 hover:text-gray-900 focus:outline-none"
                >
                  <span>{t('sensors.requiredCsvColumns')}</span>
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${csvColumnsExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {csvColumnsExpanded && (
                  <>
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr>
                            <th className="text-left font-medium text-gray-700 pr-4">{t('common.column')}</th>
                            <th className="text-left font-medium text-gray-700 pr-4">{t('common.required')}</th>
                            <th className="text-left font-medium text-gray-700 pr-4">{t('common.description')}</th>
                            <th className="text-left font-medium text-gray-700">{t('common.example')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvColumns.map((col) => (
                            <tr key={col.name} className="border-t border-gray-200">
                              <td className="py-2 pr-4 font-mono text-xs">{col.name}</td>
                              <td className="py-2 pr-4">
                                <span className={`px-2 py-0.5 text-xs rounded ${col.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>
                                  {col.required ? t('common.yes') : t('common.no')}
                                </span>
                              </td>
                              <td className="py-2 pr-4 text-gray-600">{col.description}</td>
                              <td className="py-2 font-mono text-xs text-gray-500">{col.example}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {csvExampleRow && (
                      <div className="mb-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-1">{t('sensors.exampleCsvHeaderRow')}:</h5>
                        <code className="block bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                          {csvColumns.map(c => c.name).join(',')}
                        </code>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {csvImportError && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                  {csvImportError}
                </div>
              )}
              
              <form onSubmit={handleCSVImportSubmit}>
                {/* Application Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sensors.ttiApplication')} *
                  </label>
                  <select
                    value={csvSelectedAppId}
                    onChange={(e) => setCsvSelectedAppId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    required
                  >
                    <option value="">{t('sensors.selectApplication')}</option>
                    {applicationKeys.map((appKey) => (
                      <option key={appKey.id} value={appKey.application_id}>
                        {appKey.name} ({appKey.application_id})
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* File Upload */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.csvFile')} *
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                  {csvSelectedFile && (
                    <p className="mt-1 text-sm text-gray-500">
                      {t('sensors.selected')}: {csvSelectedFile.name}
                    </p>
                  )}
                </div>
                
                {/* Import Results */}
                {csvImportResults && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('sensors.importResults')}:</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex space-x-4 mb-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-900">{csvImportResults.total}</div>
                          <div className="text-xs text-gray-500">{t('common.total')}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{csvImportResults.successful}</div>
                          <div className="text-xs text-gray-500">{t('common.successful')}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-red-600">{csvImportResults.failed}</div>
                          <div className="text-xs text-gray-500">{t('common.failed')}</div>
                        </div>
                      </div>
                      
                      <div className="max-h-60 overflow-y-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="text-left py-2 px-2">{t('sensors.deviceId')}</th>
                              <th className="text-left py-2 px-2">{t('common.status')}</th>
                              <th className="text-left py-2 px-2">{t('common.message')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {csvImportResults.results.map((result, index) => (
                              <tr key={index} className={`border-t ${result.success ? 'bg-green-50' : 'bg-red-50'}`}>
                                <td className="py-2 px-2 font-mono text-xs">{result.device_id}</td>
                                <td className="py-2 px-2">
                                  <span className={`px-2 py-0.5 text-xs rounded ${result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {result.success ? t('common.success') : t('common.failed')}
                                  </span>
                                </td>
                                <td className="py-2 px-2 text-gray-600">{result.message}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex space-x-2 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseCSVImportModal}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    {csvImportResults ? t('common.close') : t('common.cancel')}
                  </button>
                  {!csvImportResults && (
                    <button
                      type="submit"
                      disabled={csvImportLoading || !csvSelectedFile || !csvSelectedAppId}
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {csvImportLoading ? t('sensors.importing') : t('sensors.importDevices')}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Bulk Assign to Grower Modal */}
        {showBulkAssignGrowerModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('sensors.assignSensors', { count: selectedUnassignedIds.size })}
              </h3>
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('sensors.selectGrower')} *</label>
                <select
                  value={bulkAssignGrowerId ?? ''}
                  onChange={(e) => setBulkAssignGrowerId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">{t('sensors.selectGrower')}</option>
                  {growers.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setShowBulkAssignGrowerModal(false); setError(''); }}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleBulkAssignGrowerSubmit}
                  disabled={!bulkAssignGrowerId || bulkAssigning}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkAssigning ? t('sensors.assigning') : t('sensors.assign')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Sensors;

